import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EarningsBreakdown from './components/EarningsBreakdown';
import WithdrawalHistory from './components/WithdrawalHistory';
import DirectDepositSettings from './components/DirectDepositSettings';
import PayoutScheduling from './components/PayoutScheduling';
import TaxDocumentation from './components/TaxDocumentation';
import { bookingService } from '../../services/bookingService';

const PayoutManagement = () => {
  const { user, userProfile, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [earningsData, setEarningsData] = useState(null);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [pendingEarnings, setPendingEarnings] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchEarningsData = async () => {
      if (!user?.id) return;

      try {
        const bookings = await bookingService?.fetchOwnerBookings(user?.id);
        const earnings = bookingService?.calculateEarnings(bookings);
        const payoutData = await bookingService?.fetchPayoutData(user?.id);

        setEarningsData({
          totalEarnings: earnings?.monthlyRevenue * 6,
          availableBalance: payoutData?.pendingPayouts || 0,
          pendingBalance: 0,
          monthlyRevenue: earnings?.monthlyData || [],
          bookingFrequency: earnings?.totalBookings || 0,
          averageRentalRate: 85,
          seasonalTrends: {
            peak: 'Summer',
            low: 'Winter'
          }
        });

        setPendingEarnings({
          amount: payoutData?.pendingPayouts || 0,
          estimatedPayoutDate: '2026-02-15',
          bookingsCount: earnings?.completedBookings || 0,
          minimumThreshold: 50
        });

        setWithdrawalHistory(payoutData?.transactions?.map(t => ({
          id: t?.id,
          amount: parseFloat(t?.netAmount || 0),
          status: t?.payoutStatus === 'pending' ? 'pending' : 'completed',
          transactionId: t?.stripeTransferId || `TXN-${t?.id?.substring(0, 8)}`,
          requestDate: new Date(t?.createdAt)?.toISOString()?.split('T')?.[0],
          completedDate: t?.payoutDate ? new Date(t?.payoutDate)?.toISOString()?.split('T')?.[0] : null,
          method: 'Direct Deposit',
          accountLast4: '****'
        })) || []);
      } catch (error) {
        console.error('Error fetching earnings data:', error);
      }
    };

    if (isAuthenticated) {
      fetchEarningsData();
    }
  }, [user, isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'withdrawal', label: 'Withdrawal History', icon: 'History' },
    { id: 'deposit', label: 'Direct Deposit', icon: 'Building2' },
    { id: 'schedule', label: 'Payout Schedule', icon: 'Calendar' },
    { id: 'tax', label: 'Tax Documents', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Payout Management
                </h1>
                <p className="font-body text-muted-foreground">
                  Manage your earnings, withdrawals, and financial settings.
                </p>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Download"
                onClick={() => setActiveTab('withdrawal')}
                className="hidden md:flex"
              >
                Request Withdrawal
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-gradient-to-br from-primary to-primary/80 rounded-lg p-6 text-primary-foreground">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-caption text-sm opacity-90">Available Balance</p>
                  <Icon name="Wallet" size={20} strokeWidth={2} />
                </div>
                <p className="font-heading text-3xl font-bold">${earningsData?.availableBalance?.toLocaleString()}</p>
                <p className="font-caption text-xs opacity-75 mt-2">Ready to withdraw</p>
              </div>

              <div className="bg-card rounded-lg shadow-elevation-2 p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-caption text-sm text-muted-foreground">Pending Earnings</p>
                  <Icon name="Clock" size={20} strokeWidth={2} className="text-warning" />
                </div>
                <p className="font-heading text-3xl font-bold text-foreground">${pendingEarnings?.amount?.toLocaleString()}</p>
                <p className="font-caption text-xs text-muted-foreground mt-2">
                  Available {new Date(pendingEarnings?.estimatedPayoutDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              </div>

              <div className="bg-card rounded-lg shadow-elevation-2 p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-caption text-sm text-muted-foreground">Total Earnings</p>
                  <Icon name="TrendingUp" size={20} strokeWidth={2} className="text-success" />
                </div>
                <p className="font-heading text-3xl font-bold text-foreground">${earningsData?.totalEarnings?.toLocaleString()}</p>
                <p className="font-caption text-xs text-success mt-2 flex items-center space-x-1">
                  <Icon name="ArrowUp" size={12} strokeWidth={2} />
                  <span>+18% this year</span>
                </p>
              </div>
            </div>
          </div>

          {/* Mobile Withdrawal Button */}
          <Button
            variant="default"
            size="lg"
            iconName="Download"
            onClick={() => setActiveTab('withdrawal')}
            className="w-full md:hidden mb-6"
          >
            Request Withdrawal
          </Button>

          {/* Tabs Navigation */}
          <div className="bg-card rounded-lg shadow-elevation-2 mb-6 overflow-x-auto">
            <div className="flex border-b border-border min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-6 py-4 font-body font-medium transition-smooth whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} strokeWidth={2} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <EarningsBreakdown data={earningsData} pendingEarnings={pendingEarnings} />
            )}
            {activeTab === 'withdrawal' && (
              <WithdrawalHistory history={withdrawalHistory} availableBalance={earningsData?.availableBalance} />
            )}
            {activeTab === 'deposit' && <DirectDepositSettings />}
            {activeTab === 'schedule' && <PayoutScheduling />}
            {activeTab === 'tax' && <TaxDocumentation />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoutManagement;